// Mercapoli - Cart Page
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Minus, Trash2, ShoppingBag, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { useCart } from '../../context/CartContext';

const Cart = () => {
  const navigate = useNavigate();
  const { 
    items, 
    restauranteInfo, 
    subtotal, 
    costoEnvio, 
    total, 
    isEmpty,
    updateQuantity,
    removeItem,
    clearCart
  } = useCart();

  if (isEmpty) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-50 glass border-b border-border/50">
          <div className="container-app">
            <div className="flex items-center h-16 gap-4">
              <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">Mi carrito</h1>
            </div>
          </div>
        </header>

        <div className="container-app py-12 text-center">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
            <ShoppingBag className="h-12 w-12 text-muted-foreground" />
          </div>
          <h2 className="font-heading text-xl font-bold mb-2">Tu carrito está vacío</h2>
          <p className="text-muted-foreground mb-6">
            Agrega productos de tu restaurante favorito
          </p>
          <Link to="/app">
            <Button className="bg-spicy-coral hover:bg-spicy-coral/90">
              Explorar restaurantes
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="font-heading text-lg font-bold">Mi carrito</h1>
                {restauranteInfo && (
                  <p className="text-sm text-muted-foreground">{restauranteInfo.nombre}</p>
                )}
              </div>
            </div>
            <button 
              onClick={clearCart}
              className="text-sm text-destructive hover:underline"
            >
              Vaciar carrito
            </button>
          </div>
        </div>
      </header>

      <div className="container-app py-4">
        {/* Items */}
        <div className="space-y-4 mb-6">
          {items.map((item, index) => (
            <motion.div
              key={item.producto_id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex gap-4 p-4 bg-card rounded-xl border border-border"
            >
              {item.imagen && (
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={item.imagen} 
                    alt={item.nombre}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <h3 className="font-medium mb-1">{item.nombre}</h3>
                {item.notas && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Nota: {item.notas}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <span className="font-heading font-bold text-spicy-coral">
                    ${(item.precio * item.cantidad).toFixed(2)}
                  </span>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuantity(item.producto_id, item.cantidad - 1)}
                      className="p-1.5 rounded-full bg-muted hover:bg-muted/80"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="font-medium w-6 text-center">{item.cantidad}</span>
                    <button
                      onClick={() => updateQuantity(item.producto_id, item.cantidad + 1)}
                      className="p-1.5 rounded-full bg-muted hover:bg-muted/80"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removeItem(item.producto_id)}
                      className="p-1.5 rounded-full text-destructive hover:bg-destructive/10 ml-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Add more items */}
        {restauranteInfo && (
          <Link 
            to={`/app/restaurante/${restauranteInfo.restaurante_id}`}
            className="flex items-center justify-between p-4 bg-muted/50 rounded-xl mb-6 hover:bg-muted transition-colors"
          >
            <span className="text-sm">¿Agregar más productos?</span>
            <ChevronRight className="h-5 w-5" />
          </Link>
        )}

        {/* Order summary */}
        <div className="bg-card rounded-xl border border-border p-4">
          <h3 className="font-heading font-bold mb-4">Resumen del pedido</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Costo de envío</span>
              <span>${costoEnvio.toFixed(2)}</span>
            </div>
            <Separator />
            <div className="flex justify-between font-heading font-bold text-lg">
              <span>Total</span>
              <span className="text-spicy-coral">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Checkout button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent">
        <div className="container-app">
          <Button
            onClick={() => navigate('/app/checkout')}
            className="w-full bg-spicy-coral hover:bg-spicy-coral/90 py-6 rounded-full shadow-glow"
            data-testid="checkout-btn"
          >
            Continuar al pago
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Cart;
